import React, { useEffect, useState, useCallback } from 'react';
import InlineTableSkeleton from "../../components/layout/InlineTableSkeleton";
import api from '../../api/api';
import VerificationCodesPanel from '../../components/VerificationCodesPanel';
import { ScrollText, ArrowLeft, Search, ChevronLeft, ChevronRight, RefreshCw, Filter, Clock, User, Shield, UserCheck, Zap } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useNavigate } from 'react-router-dom';

const CATEGORY_CONFIG = {
    All: { color: 'slate', icon: Filter },
    Authentication: { color: 'blue', icon: Shield },
    'User Management': { color: 'purple', icon: User },
    'Employee Management': { color: 'orange', icon: UserCheck },
    Membership: { color: 'green', icon: Zap },
    Coaching: { color: 'pink', icon: UserCheck },
    'Lookup Management': { color: 'amber', icon: Filter },
};

const getBadgeClasses = (category, isDarkMode) => {
    const map = {
        Authentication: isDarkMode ? 'bg-blue-900/40 text-blue-300 border-blue-700' : 'bg-blue-50 text-blue-700 border-blue-200',
        'User Management': isDarkMode ? 'bg-purple-900/40 text-purple-300 border-purple-700' : 'bg-purple-50 text-purple-700 border-purple-200',
        'Employee Management': isDarkMode ? 'bg-orange-900/40 text-orange-300 border-orange-700' : 'bg-orange-50 text-orange-700 border-orange-200',
        Membership: isDarkMode ? 'bg-green-900/40 text-green-300 border-green-700' : 'bg-green-50 text-green-700 border-green-200',
        Coaching: isDarkMode ? 'bg-pink-900/40 text-pink-300 border-pink-700' : 'bg-pink-50 text-pink-700 border-pink-200',
        'Lookup Management': isDarkMode ? 'bg-amber-900/40 text-amber-300 border-amber-700' : 'bg-amber-50 text-amber-700 border-amber-200',
    };
    return map[category] || (isDarkMode ? 'bg-slate-700 text-slate-300 border-slate-600' : 'bg-slate-100 text-slate-600 border-slate-200');
};

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'Z');
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString();
}

export default function SystemLogs() {
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [category, setCategory] = useState('All');
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [categories, setCategories] = useState({});
    const [refreshing, setRefreshing] = useState(false);

    const fetchLogs = useCallback(async (showRefresh = false) => {
        if (showRefresh) setRefreshing(true);
        else setLoading(true);
        setError(null);
        try {
            const params = { page, per_page: 20 };
            if (category && category !== 'All') params.category = category;
            if (search) params.search = search;
            const res = await api.get('/admin/system-logs', { params });
            setLogs(res.data.logs || []);
            setTotalPages(res.data.total_pages || 1);
            setTotal(res.data.total || 0);
            setCategories(res.data.categories || {});
        } catch (err) {
            setError('Failed to fetch system logs');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [page, category, search]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        setSearch(searchInput);
    };

    const handleCategoryChange = (cat) => {
        setCategory(cat);
        setPage(1);
    };

    const allCount = Object.values(categories).reduce((a, b) => a + b, 0);

    return (
        <div className={`min-h-screen ${isDarkMode ? 'bg-surface text-slate-100' : 'bg-surface text-slate-900'}`}>
            <div className="max-w-6xl mx-auto px-4 py-8">

                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/admin/dashboard')}
                            className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-800 text-slate-400 hover:text-slate-200' : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'}`}
                            title="Back to Dashboard"
                        >
                            <ArrowLeft size={22} />
                        </button>
                        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-gradient-to-br from-indigo-600 to-purple-600' : 'bg-gradient-to-br from-indigo-500 to-purple-500'}`}>
                            <ScrollText size={24} className="text-white" />
                        </div>
                        <div>
                            <h1 className={`text-2xl font-extrabold tracking-tight ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>System Logs</h1>
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {total.toLocaleString()} total events
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => fetchLogs(true)}
                        disabled={refreshing}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-white hover:bg-slate-50 text-slate-600 border border-slate-200'}`}
                    >
                        <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>

                <VerificationCodesPanel isDarkMode={isDarkMode} />

                {/* Category Filter Tabs */}
                <div className="flex flex-wrap gap-2 mb-6">
                    {['All', ...Object.keys(CATEGORY_CONFIG).filter(k => k !== 'All')].map((cat) => {
                        const isActive = category === cat;
                        const count = cat === 'All' ? allCount : (categories[cat] || 0);
                        const Icon = CATEGORY_CONFIG[cat]?.icon || Filter;
                        return (
                            <button
                                key={cat}
                                onClick={() => handleCategoryChange(cat)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                                    isActive
                                        ? (isDarkMode ? 'bg-indigo-600 text-white border-indigo-500' : 'bg-indigo-600 text-white border-indigo-600')
                                        : (isDarkMode ? 'bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')
                                }`}
                            >
                                <Icon size={14} />
                                {cat}
                                {count > 0 && (
                                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                                        isActive
                                            ? 'bg-white/20 text-white'
                                            : (isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-500')
                                    }`}>
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Search Bar */}
                <form onSubmit={handleSearch} className="mb-6">
                    <div className="relative">
                        <Search size={18} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search logs by action, details, or email..."
                            className={`w-full pl-10 pr-4 py-2.5 rounded-lg text-sm border transition-colors ${
                                isDarkMode
                                    ? 'bg-slate-800 border-slate-700 text-slate-200 placeholder-slate-500 focus:border-indigo-500'
                                    : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-indigo-400'
                            } focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
                        />
                    </div>
                </form>

                {/* Content */}
                {loading ? (
                    <InlineTableSkeleton isDarkMode={isDarkMode} rows={8} cols={5} />
                ) : error ? (
                    <div className={`text-center py-16 rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <p className="text-red-500 font-semibold text-lg mb-2">Something went wrong</p>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{error}</p>
                        <button onClick={() => fetchLogs()} className="mt-4 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors">
                            Try Again
                        </button>
                    </div>
                ) : logs.length === 0 ? (
                    <div className={`text-center py-20 rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <ScrollText size={48} className={`mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} />
                        <p className={`text-lg font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>No logs found</p>
                        <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            {search ? 'Try adjusting your search or filters' : 'System events will appear here as they occur'}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Log Cards */}
                        <div className="space-y-2">
                            {logs.map((log) => (
                                <div
                                    key={log.LogID}
                                    className={`flex items-start gap-4 p-4 rounded-xl border transition-colors ${
                                        isDarkMode
                                            ? 'bg-slate-800/60 border-slate-700/60 hover:bg-slate-800'
                                            : 'bg-white border-slate-200 hover:bg-slate-50'
                                    }`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                            <span className={`font-semibold text-sm ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                                {log.Action}
                                            </span>
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getBadgeClasses(log.Category, isDarkMode)}`}>
                                                {log.Category}
                                            </span>
                                            {log.UserType && (
                                                <span className={`px-2 py-0.5 rounded-full text-xs ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
                                                    {log.UserType}
                                                </span>
                                            )}
                                        </div>
                                        {log.Details && (
                                            <p className={`text-sm mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                {log.Details}
                                            </p>
                                        )}
                                        {log.UserEmail && (
                                            <p className={`text-xs mt-1 flex items-center gap-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                                <User size={12} />
                                                {log.UserEmail}
                                            </p>
                                        )}
                                    </div>
                                    <div className={`flex items-center gap-1 text-xs whitespace-nowrap shrink-0 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                        <Clock size={12} />
                                        <span title={log.CreatedOn}>{timeAgo(log.CreatedOn)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between mt-6 pt-4 border-t border-dashed" style={{ borderColor: isDarkMode ? '#334155' : '#e2e8f0' }}>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                    Page {page} of {totalPages}
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page <= 1}
                                        className={`p-2 rounded-lg transition-colors ${
                                            page <= 1
                                                ? (isDarkMode ? 'text-slate-600 cursor-not-allowed' : 'text-slate-300 cursor-not-allowed')
                                                : (isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100')
                                        }`}
                                    >
                                        <ChevronLeft size={18} />
                                    </button>
                                    <button
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page >= totalPages}
                                        className={`p-2 rounded-lg transition-colors ${
                                            page >= totalPages
                                                ? (isDarkMode ? 'text-slate-600 cursor-not-allowed' : 'text-slate-300 cursor-not-allowed')
                                                : (isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100')
                                        }`}
                                    >
                                        <ChevronRight size={18} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
